/**
 * src/pages/VerifyEmailPending.js
 * "Check your email" page shown to logged-in but unverified users.
 * Provides a resend button and sign-out option.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RESEND_COOLDOWN_SECONDS = 30;

export default function VerifyEmailPending() {
  const { user, logout, resendVerification } = useAuth();
  const navigate = useNavigate();

  const [resendStatus, setResendStatus] = useState(''); // '' | sending | sent | error
  const [resendError, setResendError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Redirect if already verified
  useEffect(() => {
    if (user?.emailVerified) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = useCallback(async () => {
    setResendStatus('sending');
    setResendError('');
    try {
      await resendVerification();
      setResendStatus('sent');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not send email. Please try again.';
      setResendStatus('error');
      setResendError(msg);
    }
  }, [resendVerification]);

  function handleSignOut() {
    logout();
    navigate('/login');
  }

  if (!user) return null;

  const resendDisabled = resendStatus === 'sending' || cooldown > 0;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--content-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
          <div style={{
            width: '36px', height: '36px',
            background: 'var(--accent)',
            borderRadius: '8px'
          }} />
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Process Trails
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Client Portal
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            {/* Email icon */}
            <div style={{
              width: '48px', height: '48px',
              background: '#dbeafe',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '22px',
              color: '#2563eb'
            }}>
              &#9993;
            </div>

            <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Verify your email
            </h1>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: '1.6' }}>
              We sent a verification link to
            </p>
            <p style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '24px'
            }}>
              {user.email}
            </p>

            {/* Info box */}
            <div style={{
              background: '#dbeafe',
              border: '1px solid #93c5fd',
              borderRadius: '6px',
              padding: '12px 14px',
              marginBottom: '24px',
              fontSize: '13px',
              color: '#1e40af',
              lineHeight: '1.5',
              textAlign: 'left'
            }}>
              Click the link in the email to verify your account and access the portal.
              The link expires in 24 hours.
            </div>

            {/* Resend button */}
            <button
              className="btn btn-primary w-full"
              onClick={handleResend}
              disabled={resendDisabled}
              style={{ justifyContent: 'center', padding: '10px', marginBottom: '12px' }}
            >
              {resendStatus === 'sending' ? 'Sending…' :
               cooldown > 0 ? `Resend available in ${cooldown}s` :
               resendStatus === 'sent' ? 'Resend verification email' :
               'Resend verification email'}
            </button>

            {/* Resend success */}
            {resendStatus === 'sent' && cooldown > 0 && (
              <p style={{ fontSize: '12px', color: 'var(--success)', marginBottom: '12px' }}>
                Verification email sent! Check your inbox.
              </p>
            )}

            {/* Resend error */}
            {resendStatus === 'error' && (
              <div className="alert alert-error" style={{ marginBottom: '12px', textAlign: 'left' }}>
                {resendError}
              </div>
            )}

            {/* Sign out */}
            <button
              className="btn btn-ghost"
              onClick={handleSignOut}
              style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                textDecoration: 'underline'
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
